// Shared validation for the Add / Edit parking lot dialogs.
//
// Both dialogs submit from a plain Button rather than a form, so the MUI
// `required` props never trigger browser validation. Without this, an empty
// Sales Tax Rate reaches Firestore as NaN and an empty Total Spots makes both
// totalSpots and availability NaN.

export interface LotFormValues {
  lotName: string;
  address: string;
  latitude: string;
  longitude: string;
  salesTaxRate: string;
  maxOvertimeHours: string;
  gracePeriodMinutes: string;
  totalSpots: string;
}

export interface CurrencyRates {
  [code: string]: { rate: string };
}

export function validateLotForm(
  form: LotFormValues,
  currencies: CurrencyRates
): string[] {
  const problems: string[] = [];

  if (!form.lotName.trim()) problems.push('Lot Name is required.');
  if (!form.address.trim()) problems.push('Address is required.');

  const tax = parseFloat(form.salesTaxRate);
  if (form.salesTaxRate.trim() === '') {
    problems.push('Sales Tax Rate is required (use decimal format, e.g. 0.08 for 8%).');
  } else if (isNaN(tax) || tax < 0 || tax > 1) {
    problems.push('Sales Tax Rate must be a decimal between 0 and 1 (e.g. 0.08 for 8%).');
  }

  const spots = parseInt(form.totalSpots, 10);
  if (form.totalSpots.trim() === '') {
    problems.push('Total Parking Spots is required.');
  } else if (isNaN(spots) || spots <= 0) {
    problems.push('Total Parking Spots must be a whole number greater than zero.');
  }

  // Coordinates are optional, but half a coordinate is meaningless — the
  // dialogs only build a GeoPoint when both are present.
  const hasLat = form.latitude.trim() !== '';
  const hasLon = form.longitude.trim() !== '';
  if (hasLat !== hasLon) {
    problems.push('Enter both Latitude and Longitude, or neither.');
  } else if (hasLat && hasLon) {
    const lat = parseFloat(form.latitude);
    const lon = parseFloat(form.longitude);
    if (isNaN(lat) || lat < -90 || lat > 90) {
      problems.push('Latitude must be between -90 and 90.');
    }
    if (isNaN(lon) || lon < -180 || lon > 180) {
      problems.push('Longitude must be between -180 and 180.');
    }
  }

  if (form.maxOvertimeHours.trim() !== '') {
    const overtime = parseFloat(form.maxOvertimeHours);
    if (isNaN(overtime) || overtime < 0) {
      problems.push('Max Overtime Hours must be zero or more (leave blank for no limit).');
    }
  }

  if (form.gracePeriodMinutes.trim() !== '') {
    const grace = parseInt(form.gracePeriodMinutes, 10);
    if (isNaN(grace) || grace < 0) {
      problems.push('Grace Period must be zero or more minutes.');
    }
  }

  const codes = Object.keys(currencies);
  if (codes.length === 0) {
    problems.push('Add at least one currency with a rate.');
  }
  codes.forEach((code) => {
    const raw = currencies[code]?.rate ?? '';
    const rate = parseFloat(raw);
    if (raw.trim() === '') {
      problems.push(`Enter a rate for ${code}, or remove the currency.`);
    } else if (isNaN(rate) || rate < 0) {
      problems.push(`The ${code} rate must be zero or more.`);
    }
  });

  return problems;
}
